import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes, TableHTMLAttributes } from 'react';

// Table family — minimal token-driven wrappers around <table>.
// Bundle reference: ui-kits/officer-console.html review-queue rows.
//
// Design intent:
// - Sticky thead via position: sticky on <thead> when wrapped in scroll container.
// - Row variants: default / selected (bg-primary-soft) / flagged (bg-warn-bg/30).
// - Cell variants: default / mono (for amounts/IDs/times) / numeric (tabular-nums right-align).

export type TableProps = TableHTMLAttributes<HTMLTableElement> & {
  density?: 'comfortable' | 'compact';
};

export function Table({ density = 'comfortable', className = '', children, ...rest }: TableProps) {
  return (
    <div className="rounded-card border border-border overflow-hidden">
      <table
        className={`w-full border-collapse text-sm ${density === 'compact' ? 'text-xs' : 'text-sm'} ${className}`}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

export function TableHead({ className = '', children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead className={`bg-muted ${className}`} {...rest}>
      {children}
    </thead>
  );
}

export type TableHeadCellProps = ThHTMLAttributes<HTMLTableCellElement> & {
  align?: 'left' | 'right' | 'center';
};

export function TableHeadCell({
  align = 'left',
  className = '',
  children,
  ...rest
}: TableHeadCellProps) {
  return (
    <th
      className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-foreground-mute border-b border-border text-${align} ${className}`}
      {...rest}
    >
      {children}
    </th>
  );
}

export function TableBody({ className = '', children, ...rest }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...rest}>
      {children}
    </tbody>
  );
}

export type TableRowVariant = 'default' | 'selected' | 'flagged';
export type TableRowProps = HTMLAttributes<HTMLTableRowElement> & {
  variant?: TableRowVariant;
};

const rowVariants: Record<TableRowVariant, string> = {
  default: 'hover:bg-muted',
  selected: 'bg-primary-soft hover:bg-primary-soft',
  flagged: 'bg-warn-bg/40 hover:bg-warn-bg',
};

export function TableRow({ variant = 'default', className = '', children, onClick, ...rest }: TableRowProps) {
  const interactive = !!onClick;
  return (
    <tr
      onClick={onClick}
      className={`border-b border-border-soft last:border-b-0 ${rowVariants[variant]} ${interactive ? 'cursor-pointer' : ''} ${className}`}
      {...rest}
    >
      {children}
    </tr>
  );
}

export type TableCellVariant = 'default' | 'mono' | 'numeric';
export type TableCellProps = TdHTMLAttributes<HTMLTableCellElement> & {
  variant?: TableCellVariant;
};

const cellVariants: Record<TableCellVariant, string> = {
  default: '',
  mono: 'font-mono text-xs',
  numeric: 'font-mono text-xs tabular-nums text-right',
};

export function TableCell({ variant = 'default', className = '', children, ...rest }: TableCellProps) {
  return (
    <td className={`px-3 py-2.5 align-middle ${cellVariants[variant]} ${className}`} {...rest}>
      {children}
    </td>
  );
}
